// Fun Facts Display Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDatabase } from '../contexts/DatabaseContext';
import { funFactsData } from '../data/funFacts';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorBanner from '../components/ErrorBanner';

const FunFactsDisplay = () => {
  const [funFacts, setFunFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('All');
  const navigate = useNavigate();
  const { fetchData } = useDatabase();

  useEffect(() => {
    document.title = 'Sydney Ferris - Fun Facts';
    loadFunFacts();
  }, []);

  const loadFunFacts = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch approved facts from Firestore
      const approvedFacts = await fetchData('funFacts', { 
        where: { field: 'status', operator: '==', value: 'approved' } 
      });
      
      if (approvedFacts && approvedFacts.length > 0) {
        setFunFacts(approvedFacts);
      } else {
        // Fallback to local data if no Firestore data
        setFunFacts(funFactsData);
      }
    } catch (err) {
      console.log('Firestore not configured, using local data');
      setFunFacts(funFactsData);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', 'General', 'Science', 'History', 'Technology', 'Nature', 'Sports', 'Food', 'Other'];
  const filteredFacts = filter === 'All' ? funFacts : funFacts.filter(fact => fact.category === filter);

  return (
    <div className="min-h-[calc(100vh-5rem)] theme-bg-secondary px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-hero font-bold theme-primary mb-2 font-GoogleSans">Fun Facts</h1>
            <p className="text-sm sm:text-base lg:text-subhero theme-secondary">Interesting facts shared by the community</p>
          </div>
          <button
            onClick={() => navigate('/fun-facts')}
            className="px-4 py-2 sm:px-6 sm:py-3 theme-bg-accent text-theme-accent-contrast rounded-lg font-semibold hover:opacity-80 transition-opacity text-sm sm:text-base"
          >
            + Add Fun Fact
          </button>
        </div>

        <ErrorBanner
          title="Under Construction"
          message="This page is currently under construction. Features may not work as expected."
          type="warning"
        />

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-3 py-2 sm:px-4 rounded-lg font-medium transition-colors text-sm ${
                  filter === category
                    ? 'theme-bg-accent text-theme-accent-contrast'
                    : 'theme-bg theme-primary hover:theme-bg-secondary'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner message="Loading fun facts..." />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-8">
            <div className="theme-primary text-xl mb-4">Error loading fun facts</div>
            <p className="theme-secondary mb-4">{error}</p>
            <button 
              onClick={loadFunFacts}
              className="px-4 py-2 theme-bg-accent text-theme-accent-contrast rounded-md hover:opacity-80"
            >
              Retry
            </button>
          </div>
        )}

        {/* Fun Facts Grid */}
        {!loading && !error && filteredFacts.length === 0 && (
          <div className="text-center py-12">
            <p className="theme-secondary text-lg">No fun facts yet. Be the first to share one!</p>
          </div>
        )}

        {!loading && !error && filteredFacts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFacts.map(fact => (
              <div key={fact.id} className="card hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-3 py-1 text-xs font-medium rounded-full theme-bg-secondary theme-primary">
                    {fact.category}
                  </span>
                  <span className="text-xs theme-secondary">
                    {new Date(fact.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="theme-primary text-lg mb-4">{fact.fact}</p>
                {fact.submittedBy && (
                  <div className="flex items-center gap-2 text-sm theme-secondary">
                    <div className="w-6 h-6 bg-theme-accent rounded-full flex items-center justify-center">
                      <span className="text-xs theme-accent-contrast font-semibold">
                        {fact.submittedBy.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span>{fact.submittedBy}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FunFactsDisplay;
